/**
 * @file constants/motivationalQuotes.js
 * @description 365 motivational quotes for fitness, health, and nutrition.
 * One quote per day, synced across all users.
 * Quote of the day is determined by: dayOfYear % 365
 */

import { trackDailyQuoteViewed } from "../services/analytics";

export const MOTIVATIONAL_QUOTES = [
  // January (31 days)
  {
    quote: "The only impossible journey is the one you never begin.",
    category: "fitness",
  },
  {
    quote: "Take care of your body. It's the only place you have to live.",
    category: "health",
  },
  {
    quote:
      "Your body can stand almost anything. It's your mind that you have to convince.",
    category: "fitness",
  },
  {
    quote:
      "Discipline is choosing between what you want now and what you want most.",
    category: "nutrition",
  },
  {
    quote:
      "The pain of discipline weighs ounces while the pain of regret weighs tons.",
    category: "fitness",
  },
  { quote: "Strong is the new skinny.", category: "fitness" },
  { quote: "Don't wish for it, work for it.", category: "motivation" },
  { quote: "A healthy outside starts from the inside.", category: "health" },
  {
    quote:
      "You don't have to be great to start, but you have to start to be great.",
    category: "motivation",
  },
  {
    quote: "Your body will be around for the rest of your life. Invest in it.",
    category: "health",
  },
  { quote: "The hardest lift is getting to the gym.", category: "fitness" },
  {
    quote: "Eat clean. Train mean. Sleep like a beast.",
    category: "nutrition",
  },
  { quote: "Be the energy you want to attract.", category: "motivation" },
  { quote: "Progress not perfection.", category: "fitness" },
  {
    quote: "The only bad workout is the one that didn't happen.",
    category: "fitness",
  },
  { quote: "You are stronger than you believe.", category: "motivation" },
  { quote: "Fuel your body with food, not guilt.", category: "nutrition" },
  {
    quote: "Small daily improvements lead to epic results.",
    category: "fitness",
  },
  {
    quote: "Your future self will thank you for the effort today.",
    category: "motivation",
  },
  { quote: "Consistency is key to transformation.", category: "fitness" },
  {
    quote:
      "Health is a state of complete physical, mental and social well-being.",
    category: "health",
  },
  {
    quote: "Stop waiting for the perfect moment. Create it.",
    category: "motivation",
  },
  {
    quote: "The gym doesn't make you healthy. Consistency does.",
    category: "fitness",
  },
  { quote: "Eat to live, don't live to eat.", category: "nutrition" },
  {
    quote: "Your health is an investment, not an expense.",
    category: "health",
  },
  {
    quote: "Push yourself, because no one else is going to do it for you.",
    category: "fitness",
  },
  { quote: "Recovery is part of training.", category: "fitness" },
  { quote: "Respect the grind.", category: "motivation" },
  {
    quote: "A fit body, a calm mind, a house full of love.",
    category: "health",
  },
  {
    quote: "Don't count the days. Make the days count.",
    category: "motivation",
  },
  {
    quote: "You've already come this far. Don't give up now.",
    category: "motivation",
  },

  // February (28 days)
  {
    quote:
      "The best time to plant a tree was 20 years ago. The second best time is now.",
    category: "health",
  },
  {
    quote: "Excellence is not a skill, it's an attitude.",
    category: "motivation",
  },
  {
    quote: "Every rep counts. Every meal counts. Every day counts.",
    category: "fitness",
  },
  {
    quote:
      "Your metabolism is not your enemy. Consistency is your best friend.",
    category: "nutrition",
  },
  {
    quote: "Be patient with your body. Change takes time.",
    category: "health",
  },
  {
    quote:
      "Strength doesn't come from what you can do. It comes from overcoming what you thought you couldn't.",
    category: "fitness",
  },
  {
    quote: "Eat whole foods that actually nourish your body.",
    category: "nutrition",
  },
  { quote: "Your mind gives up before your body does.", category: "fitness" },
  { quote: "The best investment is in yourself.", category: "health" },
  { quote: "Showing up is 90% of the battle.", category: "motivation" },
  {
    quote:
      "You don't have to see the whole staircase, just take the first step.",
    category: "motivation",
  },
  { quote: "Sweat is just your fat crying.", category: "fitness" },
  {
    quote: "Drink water like your life depends on it. It does.",
    category: "nutrition",
  },
  { quote: "Your only limit is you.", category: "motivation" },
  {
    quote:
      "Rest when you're weary. Recover when you're tired. But don't give up.",
    category: "health",
  },
  {
    quote: "Habits are either your best friend or worst enemy.",
    category: "fitness",
  },
  {
    quote: "Nourish your body and it will nourish your soul.",
    category: "nutrition",
  },
  {
    quote:
      "One workout is not going to make you fit. But one workout won't break you either.",
    category: "fitness",
  },
  {
    quote: "You are one decision away from a completely different life.",
    category: "motivation",
  },
  { quote: "Feeling strong is better than looking good.", category: "fitness" },
  {
    quote: "Your diet is a bank account. Good food is good investment.",
    category: "nutrition",
  },
  { quote: "Today is the perfect day to start.", category: "motivation" },
  {
    quote: "A calorie is just a unit of energy. Use it wisely.",
    category: "nutrition",
  },
  { quote: "Your body is capable of amazing things.", category: "health" },
  {
    quote: "Success is the sum of small efforts made day in and day out.",
    category: "motivation",
  },
  { quote: "Train insane or stay the same.", category: "fitness" },
  { quote: "You can't out-train a bad diet.", category: "nutrition" },
  { quote: "Every journey begins with a single step.", category: "motivation" },

  // March (31 days)
  {
    quote: "Spring into action. Your body will thank you.",
    category: "fitness",
  },
  {
    quote: "The best body is the one that was built with discipline.",
    category: "fitness",
  },
  {
    quote: "Fuel yourself with love and good nutrition.",
    category: "nutrition",
  },
  { quote: "Your health is your wealth.", category: "health" },
  {
    quote: "Keep going. The view from the top is worth it.",
    category: "motivation",
  },
  { quote: "Sweat, sacrifice, success.", category: "fitness" },
  { quote: "Your abs are made in the kitchen.", category: "nutrition" },
  { quote: "Being healthy is a lifestyle, not a phase.", category: "health" },
  {
    quote:
      "Stop being a victim of your circumstances and start being the creator of your destiny.",
    category: "motivation",
  },
  {
    quote: "The pain of discipline is less than the pain of regret.",
    category: "fitness",
  },
  {
    quote: "You don't rise to your goals. You fall to your systems.",
    category: "fitness",
  },
  { quote: "Hydration is the foundation of fitness.", category: "nutrition" },
  {
    quote: "Your body will be craving the gym once you're consistent.",
    category: "fitness",
  },
  {
    quote: "Health is not something you find. It's something you build.",
    category: "health",
  },
  {
    quote: "The mind always gives up before the muscles do.",
    category: "fitness",
  },
  {
    quote: "Real transformation takes time. Be patient with yourself.",
    category: "health",
  },
  {
    quote: "Don't chase a perfect body. Chase a strong mind.",
    category: "motivation",
  },
  {
    quote: "Eat the rainbow. Your body deserves variety.",
    category: "nutrition",
  },
  {
    quote: "Your future depends on what you do today.",
    category: "motivation",
  },
  { quote: "Protein is your best friend.", category: "nutrition" },
  { quote: "The only shortcut is hard work.", category: "fitness" },
  {
    quote: "You are not behind. You are on your own timeline.",
    category: "motivation",
  },
  { quote: "A strong body builds a strong mind.", category: "fitness" },
  {
    quote: "Nutrition is not about deprivation. It's about nourishment.",
    category: "nutrition",
  },
  {
    quote:
      "Your body remembers every workout. Your mind remembers every excuse.",
    category: "fitness",
  },
  { quote: "Health is the crown on a well person's head.", category: "health" },
  {
    quote: "Make your body a priority, not a punishment.",
    category: "fitness",
  },
  {
    quote: "Today's consistency is tomorrow's success.",
    category: "motivation",
  },
  {
    quote: "Your muscles cry when they work. Feed them well.",
    category: "nutrition",
  },
  {
    quote: "The best time to start was yesterday. The second best time is now.",
    category: "motivation",
  },
  {
    quote: "Your health is not a destination. It's a journey.",
    category: "health",
  },

  // April (30 days)
  {
    quote:
      "Spring is the season of renewal. Renew your commitment to your health.",
    category: "health",
  },
  {
    quote: "You don't have to be perfect to be amazing.",
    category: "motivation",
  },
  { quote: "Muscles are earned, not given.", category: "fitness" },
  {
    quote: "Eat to fuel your goals, not to fill your fears.",
    category: "nutrition",
  },
  { quote: "Your health is the greatest gift.", category: "health" },
  { quote: "Progress is progress, no matter how small.", category: "fitness" },
  { quote: "Sleep is your superpower.", category: "health" },
  {
    quote:
      "The only person you should try to be better than is the person you were yesterday.",
    category: "motivation",
  },
  { quote: "Your dedication is your definition.", category: "fitness" },
  {
    quote: "Nourish yourself as if you love yourself. Because you should.",
    category: "nutrition",
  },
  { quote: "A healthy mind lives in a healthy body.", category: "health" },
  {
    quote: "Don't save your dreams for later. Start now.",
    category: "motivation",
  },
  {
    quote: "Your body is keeping score of your consistency.",
    category: "fitness",
  },
  { quote: "Food is medicine. Choose wisely.", category: "nutrition" },
  { quote: "You are stronger than your excuses.", category: "motivation" },
  { quote: "The gym is your therapy.", category: "fitness" },
  { quote: "Hydrate, nourish, elevate.", category: "nutrition" },
  {
    quote: "Your body is not the problem. Your mindset is the solution.",
    category: "health",
  },
  { quote: "Every rep brings you closer to your goal.", category: "fitness" },
  {
    quote: "Believe in yourself when nobody else does.",
    category: "motivation",
  },
  {
    quote: "Your health journey doesn't have a finish line.",
    category: "health",
  },
  { quote: "Eat clean, feel clean.", category: "nutrition" },
  {
    quote: "Push harder than yesterday or don't show up.",
    category: "fitness",
  },
  {
    quote: "You didn't come this far to only come this far.",
    category: "motivation",
  },
  { quote: "Your body is a reflection of your choices.", category: "health" },
  { quote: "Consistency beats intensity.", category: "fitness" },
  { quote: "Feeding your body is an act of self-love.", category: "nutrition" },
  { quote: "Your effort is your legacy.", category: "motivation" },
  { quote: "Strong mind, strong body.", category: "fitness" },
  {
    quote:
      "The best version of yourself is waiting on the other side of your comfort zone.",
    category: "motivation",
  },

  // May (31 days)
  {
    quote: "Summer body season starts with spring commitment.",
    category: "fitness",
  },
  {
    quote: "Your health is too important to leave to chance.",
    category: "health",
  },
  {
    quote: "Eat real food. Your body will know the difference.",
    category: "nutrition",
  },
  {
    quote: "Motivation is temporary. Discipline is forever.",
    category: "fitness",
  },
  {
    quote: "You are capable of more than you believe.",
    category: "motivation",
  },
  {
    quote: "Your cells are depending on you to make good choices.",
    category: "health",
  },
  {
    quote: "The weight will come off. The strength will come on.",
    category: "fitness",
  },
  { quote: "Protein, patience, and persistence.", category: "nutrition" },
  { quote: "Your body is your most valuable asset.", category: "health" },
  {
    quote: "Stop thinking about it and start living it.",
    category: "motivation",
  },
  {
    quote: "Every healthy choice is a declaration of self-love.",
    category: "health",
  },
  {
    quote: "Your future self is watching you right now.",
    category: "motivation",
  },
  {
    quote: "Fitness is not about being perfect. It's about being consistent.",
    category: "fitness",
  },
  {
    quote: "What you feed your body is just as important as how you train it.",
    category: "nutrition",
  },
  {
    quote: "You are never too old to chase your dreams.",
    category: "motivation",
  },
  {
    quote: "Your body is a temple. Treat it with respect.",
    category: "health",
  },
  {
    quote: "The best investment you can make is in your health.",
    category: "health",
  },
  {
    quote: "Your dedication today will be your definition tomorrow.",
    category: "fitness",
  },
  { quote: "Eat with intention. Move with purpose.", category: "nutrition" },
  { quote: "Your only competition is yourself.", category: "motivation" },
  { quote: "Strong is not just a body. It's a mindset.", category: "fitness" },
  {
    quote: "Nourish your body like you nourish your dreams.",
    category: "nutrition",
  },
  { quote: "You don't need permission to be healthy.", category: "health" },
  {
    quote:
      "The greatest glory in living lies in never falling, but in rising every time you fall.",
    category: "motivation",
  },
  {
    quote: "Your muscles are crying for you to feed them.",
    category: "nutrition",
  },
  {
    quote: "Discipline is the bridge between your goals and your reality.",
    category: "fitness",
  },
  { quote: "Your health is a choice. Choose wisely.", category: "health" },
  { quote: "You are stronger than yesterday.", category: "motivation" },
  { quote: "Recovery is when the magic happens.", category: "fitness" },
  {
    quote: "Your body is listening to your thoughts. Think positively.",
    category: "health",
  },
  {
    quote: "One more rep. One more day. One more choice.",
    category: "fitness",
  },

  // June (30 days)
  { quote: "Summer is here. Own it.", category: "fitness" },
  { quote: "Your health is not negotiable.", category: "health" },
  { quote: "Eat to thrive, not just to survive.", category: "nutrition" },
  { quote: "The only person holding you back is you.", category: "motivation" },
  {
    quote: "Your body is a work in progress. Be proud of it.",
    category: "health",
  },
  { quote: "Sweat is your body's way of saying thanks.", category: "fitness" },
  {
    quote: "Nutrition is not a punishment. It's a celebration.",
    category: "nutrition",
  },
  {
    quote: "You are enough. You are strong. You are capable.",
    category: "motivation",
  },
  {
    quote: "Your health journey is a marathon, not a sprint.",
    category: "health",
  },
  { quote: "The gym is where your excuses go to die.", category: "fitness" },
  {
    quote: "Fuel your body with foods that make you feel alive.",
    category: "nutrition",
  },
  { quote: "Your potential is limitless.", category: "motivation" },
  {
    quote: "A strong body is a strong foundation for life.",
    category: "fitness",
  },
  {
    quote: "Every meal is a choice to get better or get worse.",
    category: "nutrition",
  },
  {
    quote: "You are not trying to be perfect. You're trying to be better.",
    category: "health",
  },
  { quote: "Your dedication is your superpower.", category: "fitness" },
  { quote: "Hydrate and dominate.", category: "nutrition" },
  { quote: "Stop waiting. Start doing.", category: "motivation" },
  { quote: "Your body remembers every choice you make.", category: "health" },
  {
    quote: "The best time to take care of yourself is now.",
    category: "health",
  },
  { quote: "Pain is weakness leaving the body.", category: "fitness" },
  { quote: "Eat like you love yourself.", category: "nutrition" },
  { quote: "You are the author of your story.", category: "motivation" },
  { quote: "Your consistency is your currency.", category: "fitness" },
  { quote: "Health is a lifestyle, not a phase.", category: "health" },
  { quote: "Your muscles need you to show up for them.", category: "fitness" },
  { quote: "Nourish your body and elevate your life.", category: "nutrition" },
  { quote: "You are capable of incredible things.", category: "motivation" },
  { quote: "The best body is the one you maintain.", category: "fitness" },
  { quote: "Your health is a gift to yourself.", category: "health" },

  // July (31 days)
  {
    quote: "Summer is half over. Make the most of it.",
    category: "motivation",
  },
  { quote: "Your body is a reflection of your beliefs.", category: "health" },
  { quote: "Eat clean. Train mean. Repeat.", category: "nutrition" },
  {
    quote: "The only bad workout is the one you didn't do.",
    category: "fitness",
  },
  { quote: "Your health is your happiness.", category: "health" },
  {
    quote: "Respect the process. The results will follow.",
    category: "fitness",
  },
  { quote: "Food is fuel. Choose high-quality fuel.", category: "nutrition" },
  { quote: "You are stronger than you think.", category: "motivation" },
  {
    quote: "Your body is always listening to your habits.",
    category: "health",
  },
  { quote: "Progress over perfection.", category: "fitness" },
  {
    quote: "Nourish your body with love and good food.",
    category: "nutrition",
  },
  { quote: "Your future self will be proud of you.", category: "motivation" },
  { quote: "Strong people lift each other up.", category: "fitness" },
  {
    quote: "Your health is an investment that pays dividends.",
    category: "health",
  },
  { quote: "Consistency is the secret ingredient.", category: "fitness" },
  { quote: "Eat intentionally. Move purposefully.", category: "nutrition" },
  { quote: "You are more than capable.", category: "motivation" },
  { quote: "Your body deserves the best nutrition.", category: "health" },
  { quote: "The grind never stops.", category: "fitness" },
  { quote: "Protein builds dreams.", category: "nutrition" },
  {
    quote: "You don't need a plan B. You just need to commit to plan A.",
    category: "motivation",
  },
  { quote: "Your health is the greatest treasure.", category: "health" },
  { quote: "Every rep is a rep closer to your goal.", category: "fitness" },
  { quote: "Your body is begging you to feed it well.", category: "nutrition" },
  { quote: "You are braver than you believe.", category: "motivation" },
  { quote: "Strong mind, strong body, strong life.", category: "fitness" },
  { quote: "Your commitment is your power.", category: "health" },
  {
    quote: "Eat the food your great-grandmother would recognize.",
    category: "nutrition",
  },
  {
    quote: "The pain you feel today will be your strength tomorrow.",
    category: "fitness",
  },
  {
    quote: "Your health is not a luxury. It's a necessity.",
    category: "health",
  },
  { quote: "You are unstoppable.", category: "motivation" },

  // August (31 days)
  { quote: "August is for finishing strong.", category: "motivation" },
  { quote: "Your body is an investment in your future.", category: "health" },
  { quote: "Feed your body like you feed your dreams.", category: "nutrition" },
  {
    quote: "The only limit is the one you set for yourself.",
    category: "fitness",
  },
  { quote: "Your health journey is worth celebrating.", category: "health" },
  {
    quote: "Strength is built in the gym. Character is built in life.",
    category: "fitness",
  },
  {
    quote: "Every healthy choice is a step towards the real you.",
    category: "nutrition",
  },
  {
    quote: "You are capable of amazing things. Start today.",
    category: "motivation",
  },
  { quote: "Your body is a miracle. Treat it like one.", category: "health" },
  {
    quote: "Push yourself. Your future self will thank you.",
    category: "fitness",
  },
  { quote: "Nutrition is the foundation of health.", category: "nutrition" },
  {
    quote:
      "You don't have to be great to start, but you have to start to be great.",
    category: "motivation",
  },
  { quote: "Your dedication is your destiny.", category: "fitness" },
  { quote: "Your health is your greatest wealth.", category: "health" },
  { quote: "Eat for energy, not for emotion.", category: "nutrition" },
  { quote: "You are stronger than your doubts.", category: "motivation" },
  {
    quote: "Your body is listening. Make it a good conversation.",
    category: "health",
  },
  { quote: "Consistency is the key to transformation.", category: "fitness" },
  { quote: "Your nutrition is your medicine.", category: "nutrition" },
  {
    quote: "You are writing your own story. Make it legendary.",
    category: "motivation",
  },
  { quote: "Your health is a priority, not a hobby.", category: "health" },
  { quote: "The best version of you is waiting.", category: "fitness" },
  { quote: "Nourish yourself intentionally.", category: "nutrition" },
  { quote: "You are enough. Right now, as you are.", category: "motivation" },
  { quote: "Your body is your best investment.", category: "health" },
  {
    quote: "Every rep counts. Every meal counts. Every day counts.",
    category: "fitness",
  },
  {
    quote: "Food is information. Choose the right message.",
    category: "nutrition",
  },
  { quote: "Your persistence is your power.", category: "motivation" },
  { quote: "A healthy life is a happy life.", category: "health" },
  { quote: "The only way out is through.", category: "fitness" },
  {
    quote: "You are one choice away from a different life.",
    category: "motivation",
  },

  // September (30 days)
  { quote: "Fall into a healthy routine.", category: "health" },
  {
    quote: "Back to school means back to your fitness goals.",
    category: "fitness",
  },
  {
    quote: "Eat whole foods. Your body will thank you.",
    category: "nutrition",
  },
  { quote: "You are the architect of your health.", category: "health" },
  {
    quote: "Discipline is the difference between wanting and having.",
    category: "fitness",
  },
  { quote: "Your body is a temple. Keep it sacred.", category: "health" },
  {
    quote: "Nutrition is not a hobby. It's a lifestyle.",
    category: "nutrition",
  },
  {
    quote:
      "You are braver than you believe, stronger than you seem, and smarter than you think.",
    category: "motivation",
  },
  { quote: "Your health is your most valuable asset.", category: "health" },
  {
    quote: "The best abs are built in the gym and the kitchen.",
    category: "fitness",
  },
  { quote: "Fuel your body with whole foods.", category: "nutrition" },
  {
    quote: "You don't need permission. You need commitment.",
    category: "motivation",
  },
  {
    quote: "Your body is responding to your consistency.",
    category: "fitness",
  },
  { quote: "Every meal is a choice. Choose wisely.", category: "nutrition" },
  {
    quote: "You are strong. You are capable. You are unstoppable.",
    category: "motivation",
  },
  { quote: "Health is a journey, not a destination.", category: "health" },
  {
    quote: "Your dedication today is your success tomorrow.",
    category: "fitness",
  },
  {
    quote: "Nourish your body like you nourish your soul.",
    category: "nutrition",
  },
  {
    quote:
      "The only person you should try to be better than is who you were yesterday.",
    category: "motivation",
  },
  {
    quote: "Your body is begging for consistent training.",
    category: "fitness",
  },
  {
    quote: "Health is the crown on the well person's head.",
    category: "health",
  },
  { quote: "Eat to live, not live to eat.", category: "nutrition" },
  {
    quote: "You are capable of incredible transformation.",
    category: "motivation",
  },
  { quote: "Your strength is built one rep at a time.", category: "fitness" },
  {
    quote: "Your health is an act of rebellion against mediocrity.",
    category: "health",
  },
  {
    quote: "Protein is your best friend. Treat it like one.",
    category: "nutrition",
  },
  {
    quote: "You are unstoppable when you believe in yourself.",
    category: "motivation",
  },
  { quote: "Recovery is just as important as training.", category: "fitness" },
  {
    quote: "Your body is always working for you. Work for it.",
    category: "health",
  },
  { quote: "The best time is now.", category: "motivation" },

  // October (31 days)
  { quote: "October is for crushing your goals.", category: "motivation" },
  {
    quote: "Your health is your most important relationship.",
    category: "health",
  },
  { quote: "Eat real food. Your body will flourish.", category: "nutrition" },
  {
    quote: "The pain of discipline is nothing compared to the pain of regret.",
    category: "fitness",
  },
  {
    quote: "You are building a stronger version of yourself every day.",
    category: "health",
  },
  { quote: "Push harder. Your body is capable of more.", category: "fitness" },
  {
    quote: "Nutrition is not about restriction. It's about liberation.",
    category: "nutrition",
  },
  {
    quote: "Your future depends on the choices you make right now.",
    category: "motivation",
  },
  {
    quote: "Your health is a choice you make every single day.",
    category: "health",
  },
  { quote: "Strong mind, strong body, strong spirit.", category: "fitness" },
  { quote: "Feed your body with love and intention.", category: "nutrition" },
  { quote: "You are more powerful than your excuses.", category: "motivation" },
  {
    quote: "Your body is a living, breathing reflection of your lifestyle.",
    category: "health",
  },
  { quote: "Every rep is a rep closer to greatness.", category: "fitness" },
  { quote: "Your health is a verb, not a noun.", category: "health" },
  { quote: "Hydration is not negotiable.", category: "nutrition" },
  { quote: "You are destined for greatness.", category: "motivation" },
  { quote: "Your dedication is your definition.", category: "fitness" },
  {
    quote: "Eat to fuel your life, not to fill your life.",
    category: "nutrition",
  },
  {
    quote: "Your body is always listening. Speak to it with kindness.",
    category: "health",
  },
  {
    quote: "The best time to start was yesterday. The second best time is now.",
    category: "motivation",
  },
  { quote: "Your health is your greatest achievement.", category: "health" },
  { quote: "Consistency is the greatest teacher.", category: "fitness" },
  { quote: "Your nutrition is your foundation.", category: "nutrition" },
  {
    quote: "You are stronger than you ever thought possible.",
    category: "motivation",
  },
  { quote: "Your body is always keeping score.", category: "fitness" },
  {
    quote: "Health is not a destination. It's a way of living.",
    category: "health",
  },
  {
    quote: "Eat intentionally. Move purposefully. Live fully.",
    category: "nutrition",
  },
  {
    quote: "Your potential is limitless. Start proving it.",
    category: "motivation",
  },
  { quote: "Your strength is inspiration to others.", category: "fitness" },
  { quote: "Your body deserves your respect.", category: "health" },

  // November (30 days)
  { quote: "November is for gratitude and goals.", category: "motivation" },
  { quote: "Be grateful for your body and treat it well.", category: "health" },
  { quote: "Nutrition is self-love in every bite.", category: "nutrition" },
  { quote: "Your health is something to be grateful for.", category: "health" },
  {
    quote: "The only way to reach your goals is through consistent action.",
    category: "fitness",
  },
  { quote: "Your dedication is your superpower.", category: "motivation" },
  { quote: "Eat whole foods. Your body will glow.", category: "nutrition" },
  { quote: "You are capable of more than you know.", category: "motivation" },
  {
    quote: "Your health is not a luxury. It's a necessity.",
    category: "health",
  },
  { quote: "Strong people build strong communities.", category: "fitness" },
  { quote: "Nourish yourself with love and good food.", category: "nutrition" },
  { quote: "Your future self is counting on you.", category: "motivation" },
  { quote: "Your body is a gift. Honor it.", category: "health" },
  {
    quote: "Every meal is an opportunity to choose health.",
    category: "nutrition",
  },
  { quote: "You are unstoppable.", category: "motivation" },
  {
    quote: "Your fitness is a reflection of your commitment.",
    category: "fitness",
  },
  { quote: "Health is wealth. Invest wisely.", category: "health" },
  {
    quote: "Protein, patience, and persistence will win the day.",
    category: "nutrition",
  },
  { quote: "You are stronger than your fear.", category: "motivation" },
  {
    quote: "Your body is your sanctuary. Treat it like one.",
    category: "health",
  },
  { quote: "Consistency beats perfection every time.", category: "fitness" },
  { quote: "Your health is your happiness.", category: "health" },
  {
    quote: "Fuel your body with foods that make you feel amazing.",
    category: "nutrition",
  },
  {
    quote: "You don't have to see the whole path. Just take the next step.",
    category: "motivation",
  },
  { quote: "Your dedication is your legacy.", category: "fitness" },
  {
    quote: "Your body is a masterpiece. Paint it with purpose.",
    category: "health",
  },
  { quote: "Eat to thrive, not just survive.", category: "nutrition" },
  { quote: "You are more than capable.", category: "motivation" },
  { quote: "Your health journey is unique and beautiful.", category: "health" },
  {
    quote: "The best time to invest in your health is now.",
    category: "motivation",
  },

  // December (31 days)
  {
    quote: "December is for finishing the year strong.",
    category: "motivation",
  },
  {
    quote: "Your health is your greatest gift to yourself.",
    category: "health",
  },
  { quote: "Eat consciously. Live vibrantly.", category: "nutrition" },
  { quote: "You are capable of incredible things.", category: "motivation" },
  { quote: "Your body is always ready. Are you?", category: "fitness" },
  { quote: "Health is the real wealth.", category: "health" },
  { quote: "Nourish your body. Nurture your soul.", category: "nutrition" },
  { quote: "You are stronger than your excuses.", category: "motivation" },
  { quote: "Your consistency is your superpower.", category: "fitness" },
  { quote: "Every healthy choice is a victory.", category: "health" },
  { quote: "Protein builds champions.", category: "nutrition" },
  {
    quote: "Your dedication will become your definition.",
    category: "motivation",
  },
  {
    quote: "Your body is always listening to your commitment.",
    category: "fitness",
  },
  { quote: "Health is not a trend. It's a lifestyle.", category: "health" },
  { quote: "Eat like you love yourself.", category: "nutrition" },
  {
    quote: "You are capable of amazing transformation.",
    category: "motivation",
  },
  { quote: "Your strength inspires others.", category: "fitness" },
  {
    quote: "Your health is your most valuable investment.",
    category: "health",
  },
  { quote: "Every meal is a step towards your goals.", category: "nutrition" },
  { quote: "You are unstoppable when you believe.", category: "motivation" },
  {
    quote: "Your body is a work of art. Treat it with respect.",
    category: "health",
  },
  { quote: "Consistency is your best trainer.", category: "fitness" },
  { quote: "Fuel yourself with intention and passion.", category: "nutrition" },
  { quote: "Your potential is infinite.", category: "motivation" },
  { quote: "Your dedication is your destiny.", category: "fitness" },
  { quote: "Health is a choice you make every day.", category: "health" },
  { quote: "Eat to become your best self.", category: "nutrition" },
  { quote: "You are braver than you believe.", category: "motivation" },
  { quote: "Your body is always ready for greatness.", category: "fitness" },
  {
    quote: "End the year strong. Start the next stronger.",
    category: "motivation",
  },
  {
    quote: "Your health is your legacy. Build it proudly.",
    category: "health",
  },
];

/**
 * Get today's quote based on the day of the year
 * All users see the same quote on the same day
 * @returns {object} { quote, category }
 */
export function getTodaysQuote() {
  const now = new Date();

  // Calculate day of year (0-364)
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now - start;
  const oneDay = 1000 * 60 * 60 * 24;
  const dayOfYear = Math.floor(diff / oneDay);

  // Get quote for this day (wraps around if somehow > 365)
  const quoteIndex = dayOfYear % MOTIVATIONAL_QUOTES.length;
  trackDailyQuoteViewed(MOTIVATIONAL_QUOTES[quoteIndex]);
  return MOTIVATIONAL_QUOTES[quoteIndex];
}

/**
 * Get quote for a specific day (for testing)
 * @param {number} dayOfYear 0-364
 * @returns {object}
 */
export function getQuoteForDay(dayOfYear) {
  const index = dayOfYear % MOTIVATIONAL_QUOTES.length;
  return MOTIVATIONAL_QUOTES[index];
}
